/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import { Box, useTheme, IconButton, Stack, ThemeProvider } from '@mui/material';
import { getDefaultAlertProps } from '../../utils/utils';
import { MyAlert } from '../UI/MyAlert';
import { IAlertProps } from '../../interface/IAlertProps';
import {
  MAIN_COLOR_STRING,
  PAGE_SIZE_DEVICES,
  START_PAGE_SIZE_DEVICES,
} from '../../constant/constants';
import { BackDropDiv } from '../backdrop/BackDropDiv';
import { IApiResult } from '../../interface/IApiResult';
import { AlertSeverityEnum } from '../../enum/AlertSeverityEnum';
import { MyTextFiled } from '../UI/MyTextField';
import { FactoryBodyHeaderDiv } from '../body-header/FactoryBodyHeaderDiv';
import { CloudUserDto } from '../../dto/CloudUserDto';
import { apiGetCloudUser } from '../../service/cloud-user/apiGetCloudUser';
import { apiDeleteCloudUser } from '../../service/cloud-user/apiDeleteCloudUser';
import { apiAddCloudUser } from '../../service/cloud-user/apiAddCloudUser';

export const CloudUserDiv = () => {
  const [pageSize, setPageSize] = React.useState(START_PAGE_SIZE_DEVICES);
  const { t } = useTranslation();
  const [backDrop, setBackDrop] = useState(false);
  const [alertProps, setAlertProps] = useState<IAlertProps>(
    getDefaultAlertProps()
  );
  const [cloudUserDto, setCloudUserDto] = useState<CloudUserDto>({
    id: -1,
    login: '',
    password: '',
    token: '',
    name: '',
    organization: '',
    createDate: -1,
  });
  const [cloudUserDtos, setCloudUserDtos] = useState<CloudUserDto[]>([]);

  const getCloudUser = async () => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetCloudUser();
    if (apiResult.isSuccess) {
      setCloudUserDtos(apiResult.data.cloud);
    } else {
      setAlertProps({
        message: t('errotServer'),
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const deleteCloudUser = async (dto: CloudUserDto) => {
    const apiResult: IApiResult = await apiDeleteCloudUser(dto);
    if (apiResult.isSuccess) {
      getCloudUser();
    } else {
      setAlertProps({
        message: t('errotServer'),
        severity: AlertSeverityEnum.error,
      });
    }
  };

  const addCloudUser = async (dto: CloudUserDto) => {
    const apiResult: IApiResult = await apiAddCloudUser(dto);
    if (apiResult.isSuccess) {
      setCloudUserDto({
        id: -1,
        login: '',
        password: '',
        token: '',
        name: '',
        organization: '',
        createDate: -1,
      });
      getCloudUser();
    } else {
      setAlertProps({
        message: t('errotServer'),
        severity: AlertSeverityEnum.error,
      });
    }
  };

  const [checkHelper, setCheckHelper] = useState(false);

  const handelCreate = async () => {
    if (
      cloudUserDto?.login === '' ||
      cloudUserDto?.password === '' ||
      cloudUserDto?.organization === ''
    ) {
      setCheckHelper(true);
      return;
    }
    await addCloudUser(cloudUserDto);
    setCheckHelper(false);
  };

  const theme = useTheme();

  useEffect(() => {
    getCloudUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'organization',
      headerName: t('headerCloudOrganization'),
      renderHeader: () => <strong>{t('headerCloudOrganization')}</strong>,
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (row) => row.row.organization,
    },
    {
      field: 'login',
      headerName: t('headerCloudLogin'),
      renderHeader: () => <strong>{t('headerCloudLogin')}</strong>,
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (row) => row.row.login,
    },
    {
      field: 'token',
      headerName: t('headerCloudToken'),
      renderHeader: () => <strong>{t('headerCloudToken')}</strong>,
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (row) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div
          title={t('tooltipCopy')}
          style={{ cursor: 'copy' }}
          onClick={() => {
            navigator.clipboard.writeText(row.row.token);
          }}
        >
          {row.row.token}
        </div>
      ),
    },
    {
      field: 'id',
      type: 'actions',
      width: 35,
      headerName: '',
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (row) => (
        <Stack direction="row" width="100%" justifyContent="center">
          <IconButton
            size="small"
            title={t('tooltipDelete')}
            onClick={() => deleteCloudUser(row.row)}
          >
            <DeleteForeverIcon
              fontSize="small"
              sx={{
                color: 'red',
              }}
            />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const other = {
    autoHeight: true,
    showCellRightBorder: true,
    showColumnRightBorder: true,
  };

  return (
    <div>
      <BackDropDiv open={backDrop} />
      <MyAlert
        message={alertProps.message}
        severity={alertProps.severity}
        onClose={() => setAlertProps({ ...alertProps, message: '' })}
      />
      <Stack width="100%" alignItems="center" marginBottom="20px" spacing={2}>
        <Stack width="90%">
          <FactoryBodyHeaderDiv title={t('cloudUser')} />
        </Stack>
        <Stack width="90%" spacing={1}>
          <Stack direction="row" spacing={1}>
            <MyTextFiled
              value={cloudUserDto.organization}
              autoComplete="off"
              onChange={(e) =>
                setCloudUserDto({
                  ...cloudUserDto,
                  organization: e.target.value,
                })
              }
              size="small"
              error={checkHelper}
              label={t('headerCloudOrganization')}
            />
            <MyTextFiled
              value={cloudUserDto.login}
              onChange={(e) =>
                setCloudUserDto({
                  ...cloudUserDto,
                  login: e.target.value,
                })
              }
              autoComplete="off"
              size="small"
              error={checkHelper}
              label={t('headerCloudLogin')}
            />
            <MyTextFiled
              value={cloudUserDto.password}
              onChange={(e) =>
                setCloudUserDto({
                  ...cloudUserDto,
                  password: e.target.value,
                })
              }
              autoComplete="off"
              size="small"
              error={checkHelper}
              label={t('headerCloudPassword')}
            />
            <Stack alignItems="center">
              <IconButton
                sx={{ color: MAIN_COLOR_STRING }}
                onClick={handelCreate}
              >
                <AddIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Box
            sx={{
              '& .app-theme-cell': {
                fontFamily: 'sans-serif',
              },
            }}
          >
            <ThemeProvider theme={theme}>
              <DataGrid
                sx={{
                  '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                    py: 1,
                    fontSize: '100px',
                  },
                  '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                    py: '5px',
                  },
                  '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                    py: '10px',
                  },
                  '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within':
                    {
                      outline: 'none !important',
                    },
                }}
                getRowHeight={() => 'auto'}
                columns={columns}
                rows={cloudUserDtos}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...other}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={PAGE_SIZE_DEVICES}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
              />
            </ThemeProvider>
          </Box>
        </Stack>
      </Stack>
    </div>
  );
};
